import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  CmsLetterImageSignaturesComponent,
  CMSLetterSignatureComponent,
  CMSLetterUserBlockComponent,
  CMSMeritLetterTableComponent,
  CMSPerformanceTableComponent,
  RenderedGmmLineComponent,
  RenderedGmmSpacerComponent
} from '@coin-siemens-energy/letter-print-page';
import { NumberFormatterPipe, PureFunctionPipe, StripParagraphsPipe } from '@coin/shared/core/util-pipes';
import { AuthGmsAngularClientModule } from '@coin/shared/auth/feature';
import { AllCmsNewsItemsComponent } from './news/all-cms-news-items/all-cms-news-items.component';
import { CmsButtonComponent } from './news/cms-button/cms-button.component';
import { CompilationComponent } from './news/compilation/compilation.component';
import { DownloadFileComponent } from './emails/download-file/download-file.component';
import { EmailButtonComponent } from './emails/email-button/email-button.component';
import { EmailIndexListComponent } from './emails/email-index-list/email-index-list.component';
import { EmailMainBodyImageTextComponent } from './emails/email-main-body-image-text/email-main-body-image-text.component';
import { EmailSingleDoubleImageComponent } from './emails/email-single-double-image/email-single-double-image.component';
import { EmailSliderComponent } from './shared/email-slider/email-slider.component';
import { EmailVideoComponent } from './emails/email-video/email-video.component';
import { EquityCmsCardsComponent } from './equity/equity-cms-cards/equity-cms-cards.component';
import { EquityCmsChapterComponent } from './equity/equity-cms-chapter/equity-cms-chapter.component';
import { EquityCmsChapterOverviewComponent } from './equity/equity-cms-chapter-overview/equity-cms-chapter-overview.component';
import { EquityCmsFullWidthpartWithImageComponent } from './equity/equity-cms-full-widthpart-with-image/equity-cms-full-widthpart-with-image.component';
import { EquityExpandBoxComponent } from './equity/equity-expand-box/equity-expand-box.component';
import { EquityExpandLinksboxComponent } from './equity/equity-expand-linksbox/equity-expand-linksbox.component';
import { HtmlEditorComponent } from './shared/html-editor/html-editor.component';
import { ImageSliderComponent } from './news/image-slider/image-slider.component';
import { LineComponent } from './news/line/line.component';
import { NewsHeadlineComponent } from './news/news-headline/news-headline.component';
import { NumbersComponent } from './news/numbers/numbers.component';
import { PictureContainerComponent } from './news/picture-container/picture-container.component';
import { QuoteComponent } from './news/quote/quote.component';
import { SpacerComponent } from './news/spacer/spacer.component';
import { TextBlockComponent } from './news/text-block/text-block.component';
import { TextImageLinkComponent } from './news/text-image-link/text-image-link.component';
import { LegacyComponentsModule } from '@coin/shared/core/feature-legacy-components';
import {
  IconButtonComponent,
  V2ButtonComponent,
  V2CheckboxComponent,
  V2DialogModule,
  V2NumberInputComponent,
  V2TextInputComponent
} from '@coin/shared/core/ui-storybook';
import { TranslateModule } from '@ngx-translate/core';
import { DragScrollComponent, DragScrollItemDirective } from 'ngx-drag-scroll';
import { SimplebarAngularModule } from 'simplebar-angular';
import { BasicEmailComponent } from './emails/basic-email/basic-email.component';
import { CmsEditEmailComponent } from './emails/cms-edit-email/cms-edit-email.component';
import { EmailFootnotesComponent } from './emails/email-footnotes/email-footnotes.component';
import { EmailHeaderComponent } from './emails/email-header/email-header.component';
import { EmailPreviewComponent } from './emails/email-preview/email-preview.component';
import { EmailQuoteComponent } from './emails/email-quote/email-quote.component';
import { CmsEditNewsComponent } from './news/cms-edit-news/cms-edit-news.component';
import { PreviewComponent } from './news/preview/preview.component';
import { SelectNewContentComponent } from './news/select-new-content/select-new-content.component';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';

@NgModule({
  imports: [
    CommonModule,
    LegacyComponentsModule,
    SimplebarAngularModule,
    MatExpansionModule,
    MatRadioModule,
    MatSliderModule,
    MatBadgeModule,
    MatProgressSpinnerModule,
    DragDropModule,
    MatInputModule,
    FormsModule,
    TranslateModule.forChild(),
    MatListModule,
    StripParagraphsPipe,
    PureFunctionPipe,
    MatTooltipModule,
    MatTabsModule,
    MatSelectModule,
    MatCheckboxModule,
    DragScrollComponent,
    DragScrollItemDirective,
    MatIconModule,
    NumberFormatterPipe,
    MatCardModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    V2NumberInputComponent,
    V2CheckboxComponent,
    V2TextInputComponent,
    IconButtonComponent,
    V2DialogModule,
    V2ButtonComponent,
    CMSLetterSignatureComponent,
    CMSLetterUserBlockComponent,
    CMSPerformanceTableComponent,
    CMSMeritLetterTableComponent,
    CmsLetterImageSignaturesComponent,
    RenderedGmmSpacerComponent,
    RenderedGmmLineComponent,
    V2DialogModule,
    TextBlockComponent,
    ImageSliderComponent,
    QuoteComponent,
    SpacerComponent,
    PictureContainerComponent,
    CompilationComponent,
    NumbersComponent,
    TextImageLinkComponent,
    HtmlEditorComponent,
    EmailSingleDoubleImageComponent,
    NewsHeadlineComponent,
    DownloadFileComponent,
    CmsButtonComponent,
    EmailIndexListComponent,
    LineComponent,
    EmailButtonComponent,
    EmailMainBodyImageTextComponent,
    EmailSliderComponent,
    TextBlockComponent,
    EquityExpandBoxComponent,
    EquityExpandLinksboxComponent,
    EquityCmsChapterComponent,
    EquityCmsChapterOverviewComponent,
    EquityCmsCardsComponent,
    EquityCmsFullWidthpartWithImageComponent,
    AllCmsNewsItemsComponent,
    EmailVideoComponent,
    CmsEditEmailComponent,
    CmsEditNewsComponent,
    BasicEmailComponent,
    PreviewComponent,
    EmailPreviewComponent,
    SelectNewContentComponent,
    EmailHeaderComponent,
    EmailFootnotesComponent,
    EmailQuoteComponent
  ],
  exports: [
    CmsEditEmailComponent,
    CmsEditNewsComponent,
    BasicEmailComponent,
    PreviewComponent,
    EmailPreviewComponent,
    SelectNewContentComponent,
    CmsButtonComponent,
    EmailHeaderComponent,
    EmailFootnotesComponent,
    EmailQuoteComponent
  ],
  providers: [{ provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' }]
})
export class CmsComponentsModule {
  static forTest() {
    return CmsComponentsTestModule;
  }
}

@NgModule({ imports: [CmsComponentsModule, AuthGmsAngularClientModule.forTest()] })
class CmsComponentsTestModule {}
